import { useCallback, useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Expand } from "lucide-react";
import PortfolioLightbox from "@/components/site/PortfolioLightbox";

const SLIDES = [
  {
    src: "/portfolio/samples-new/sample1-1.png",
    label: "Black & White · Sample 01",
    chip: "bg-stone-900 text-white",
  },
  {
    src: "/portfolio/samples-new/sample2-1.png",
    label: "Black & White · Sample 02",
    chip: "bg-stone-800 text-white",
  },
  {
    src: "https://customer-assets-0z36b82j.emergentagent.net/job_dmv-layouts/artifacts/ul9liqov_Screenshot%202026-08-15%20at%201.01.40%E2%80%AFPM.png",
    label: "Colorized",
    chip: "bg-teal-700 text-white",
  },
  {
    src: "https://customer-assets-0z36b82j.emergentagent.net/job_dmv-layouts/artifacts/dnx17hij_ChatGPT%20Image%20Aug%2015%2C%202026%20at%2001_07_14%20PM.png",
    label: "3D · Colorized",
    chip: "bg-amber-600 text-white",
  },
  {
    src: "/portfolio/cleaned/meriden.png",
    label: "3D Aerial Site",
    chip: "bg-emerald-700 text-white",
  },
  {
    src: "https://customer-assets-0z36b82j.emergentagent.net/job_dmv-layouts/artifacts/p3lqzvw0_ChatGPT%20Image%20Aug%2015%2C%202026%20at%2001_29_41%20PM.png",
    label: "3D Aerial Estate",
    chip: "bg-emerald-800 text-white",
  },
];

export default function HeroFinishes() {
  const [active, setActive] = useState(0);
  const [lightbox, setLightbox] = useState({ open: false });
  const total = SLIDES.length;
  const pausedRef = useRef(false);

  const goTo = useCallback(
    (i) => setActive(((i % total) + total) % total),
    [total]
  );

  const openLightbox = () => {
    pausedRef.current = true;
    setLightbox({ open: true });
  };
  const closeLightbox = () => {
    setLightbox({ open: false });
    pausedRef.current = false;
  };

  useEffect(() => {
    const t = setInterval(() => {
      if (!pausedRef.current) setActive((a) => (a + 1) % total);
    }, 4500);
    return () => clearInterval(t);
  }, [total]);

  return (
    <div
      className="relative"
      data-testid="hero-finishes"
      onMouseEnter={() => (pausedRef.current = true)}
      onMouseLeave={() => (pausedRef.current = false)}
    >
      {/* Stage card */}
      <div className="relative bg-white border border-stone-200 rounded-[2.25rem] p-3 lg:p-5 shadow-[0_30px_60px_-15px_rgba(15,23,42,0.18)]">
        <div className="relative rounded-[1.5rem] overflow-hidden bg-[#FAF7F2] aspect-[4/3]">
          <button
            type="button"
            onClick={openLightbox}
            aria-label={`Enlarge ${SLIDES[active].label}`}
            data-testid="hero-finishes-enlarge"
            className="absolute inset-0 w-full h-full cursor-zoom-in group"
          >
            {SLIDES.map((s, i) => (
              <img
                key={s.src}
                src={s.src}
                alt={s.label}
                draggable={false}
                className={`absolute inset-0 w-full h-full object-contain transition-opacity duration-700 ${
                  i === active ? "opacity-100" : "opacity-0"
                }`}
                data-testid={`hero-finish-slide-${i}`}
              />
            ))}
            <span className="absolute top-3 right-14 inline-flex items-center gap-1 bg-stone-900/85 text-white px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider opacity-0 group-hover:opacity-100 transition-opacity">
              <Expand className="h-3 w-3" />
              Click to enlarge
            </span>
          </button>

          {/* Arrows */}
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); goTo(active - 1); }}
            data-testid="hero-finishes-prev"
            aria-label="Previous finish"
            className="absolute top-1/2 left-2 -translate-y-1/2 h-9 w-9 rounded-full bg-white/95 backdrop-blur border border-stone-200 shadow-sm hover:bg-white hover:-translate-x-0.5 transition-all flex items-center justify-center z-10"
          >
            <ChevronLeft className="h-4 w-4 text-stone-800" />
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); goTo(active + 1); }}
            data-testid="hero-finishes-next"
            aria-label="Next finish"
            className="absolute top-1/2 right-2 -translate-y-1/2 h-9 w-9 rounded-full bg-white/95 backdrop-blur border border-stone-200 shadow-sm hover:bg-white hover:translate-x-0.5 transition-all flex items-center justify-center z-10"
          >
            <ChevronRight className="h-4 w-4 text-stone-800" />
          </button>
        </div>

        {/* Footer: dots + counter */}
        <div className="flex items-center justify-end gap-4 px-2 pt-3 pb-1">
          <div className="flex items-center gap-1.5">
            {SLIDES.map((s, i) => (
              <button
                key={s.src}
                type="button"
                onClick={() => goTo(i)}
                data-testid={`hero-finishes-dot-${i}`}
                aria-label={`Show ${s.label}`}
                className={`h-1.5 rounded-full transition-all ${
                  i === active ? "w-6 bg-teal-700" : "w-1.5 bg-stone-300 hover:bg-stone-400"
                }`}
              />
            ))}
          </div>
          <div className="text-[11px] font-mono text-stone-500">
            {String(active + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}
          </div>
        </div>
      </div>

      <PortfolioLightbox
        open={lightbox.open}
        items={SLIDES.map((s) => ({ src: s.src, alt: s.label }))}
        index={active}
        onIndexChange={(i) => setActive(i)}
        onClose={closeLightbox}
      />
    </div>
  );
}
